import * as Sentry from "@sentry/react";
import { doc, onSnapshot } from "firebase/firestore";
import { useEffect, useRef, useState } from "react";
import { db } from "../lib/firebase.js";
import * as storage from "../lib/storage.js";
import type { SyncAccountData } from "../types/index.js";

interface UseSyncAccountOptions {
  syncAccountId: string | null;
  onRemoteChange: (data: SyncAccountData) => void;
}

export function useSyncAccount({ syncAccountId, onRemoteChange }: UseSyncAccountOptions) {
  const [syncing, setSyncing] = useState(false);
  const onRemoteChangeRef = useRef(onRemoteChange);
  onRemoteChangeRef.current = onRemoteChange;

  useEffect(() => {
    if (!syncAccountId) {
      setSyncing(false);
      return;
    }

    setSyncing(true);
    let isFirst = true;

    const unsubscribe = onSnapshot(
      doc(db, "syncAccounts", syncAccountId),
      (snapshot) => {
        if (!snapshot.exists()) {
          setSyncing(false);
          return;
        }

        // Skip the first snapshot (initial load) to avoid overwriting
        // local state with stale server data on mount
        if (isFirst) {
          isFirst = false;
          return;
        }

        const data = snapshot.data() as SyncAccountData;
        const local = { displayName: storage.getDisplayName(), lists: storage.getLists() };

        // Only apply if data actually changed
        const remoteJSON = JSON.stringify({ displayName: data.displayName, lists: data.lists });
        const localJSON = JSON.stringify(local);

        if (remoteJSON !== localJSON) {
          storage.applyRemoteSync({ displayName: data.displayName, lists: data.lists });
          onRemoteChangeRef.current(data);
        }
      },
      (err) => {
        Sentry.captureException(err);
        setSyncing(false);
      },
    );

    return unsubscribe;
  }, [syncAccountId]);

  return { syncing };
}
